interface SMSSendResponse {
  success: boolean;
  message: string;
  error?: string;
  record_id?: string;
  status?: string;
}

class SMSApiService {
  /**
   * Normalize phone number to E.164 format (e.g., +12345678900)
   * Handles various input formats:
   * - (123) 456-7890
   * - 123-456-7890
   * - 123.456.7890
   * - 123 456 7890
   * - 1234567890
   * - 11234567890 (with country code 1)
   * - +11234567890 (already in E.164)
   */
  private normalizeToE164(phoneNumber: string | null | undefined): string {
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    // Trim whitespace
    let cleaned = phoneNumber.trim();
    
    if (!cleaned) {
      throw new Error('Phone number is empty');
    }

    // Remove all non-digit characters except leading +
    cleaned = cleaned.replace(/[^\d+]/g, '');
    
    // Check if already in E.164 format (+1XXXXXXXXXX)
    if (cleaned.startsWith('+')) {
      // Already has + prefix
      cleaned = cleaned.substring(1); // Remove the +
      
      // Validate it's a valid E.164 number
      if (!/^\d+$/.test(cleaned)) {
        throw new Error(`Invalid phone number format: ${phoneNumber}`);
      }
      
      // If it starts with 1 and is 11 digits, it's a valid US number
      if (cleaned.startsWith('1') && cleaned.length === 11) {
        return `+${cleaned}`; // Already in correct format
      }
      
      // If it's 10 digits, assume US and add country code
      if (cleaned.length === 10) {
        return `+1${cleaned}`;
      }
      
      // Otherwise, try to return as-is (might be international)
      if (cleaned.length >= 10 && cleaned.length <= 15) {
        return `+${cleaned}`;
      }
      
      throw new Error(`Invalid phone number length after +: ${cleaned.length} digits. Expected 10-15 digits.`);
    }
    
    // No + prefix, treat as US number
    // Validate it's all digits
    if (!/^\d+$/.test(cleaned)) {
      throw new Error(`Invalid phone number format: ${phoneNumber}`);
    }
    
    // Handle 11-digit numbers starting with 1 (US country code)
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    
    // Handle 10-digit numbers (US numbers without country code)
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    
    // Handle other lengths - might have formatting issues
    if (cleaned.length < 10) {
      throw new Error(`Phone number too short: ${cleaned.length} digits. Expected at least 10 digits. Original: ${phoneNumber}`);
    }
    
    if (cleaned.length > 15) {
      throw new Error(`Phone number too long: ${cleaned.length} digits. Expected at most 15 digits. Original: ${phoneNumber}`);
    }
    
    // Try to extract a valid US number pattern
    // Look for 10 consecutive digits (might have extra formatting)
    const match = cleaned.match(/\d{10}/);
    if (match) {
      return `+1${match[0]}`;
    }
    
    // If all else fails, try to use last 10 digits (common for numbers with extensions)
    if (cleaned.length > 10) {
      const last10 = cleaned.substring(cleaned.length - 10);
      console.warn(`Phone number had ${cleaned.length} digits, using last 10: ${last10}. Original: ${phoneNumber}`);
      return `+1${last10}`;
    }
    
    throw new Error(`Unable to normalize phone number: ${phoneNumber} (${cleaned.length} digits)`);
  }

  private getFromNumber(): string {
    // Get from number from environment variable, or use default
    const fromNumber = process.env.REACT_APP_SMS_FROM_NUMBER || 
                       process.env.SMS_FROM_NUMBER ||
                       '+12014222696'; // Default number
    return fromNumber;
  }

  private getBaseUrl(): string {
    return 'https://hr-backend-pipeline.vercel.app/api/telnyx/sendSMS';
  }

  private getInternalKey(): string {
    const key = process.env.REACT_APP_INTERNAL_KEY ?? process.env.INTERNAL_KEY;
    if (!key) {
      throw new Error('INTERNAL_KEY (or REACT_APP_INTERNAL_KEY) is required for sending SMS');
    }
    return key;
  }

  async sendSMS(to: string, text: string, fromNumberOverride?: string): Promise<SMSSendResponse> {
    try {
      // Normalize phone number to E.164 format
      const normalizedTo = this.normalizeToE164(to);
      
      const baseUrl = this.getBaseUrl();
      // Use override if provided, otherwise use default from number
      const fromNumber = fromNumberOverride || this.getFromNumber();
      const normalizedFrom = this.normalizeToE164(fromNumber);

      console.log('Sending SMS request via backend server:', {
        originalTo: to,
        normalizedTo: normalizedTo,
        from: normalizedFrom,
        fromOverride: !!fromNumberOverride,
        text: text,
        baseUrl: baseUrl
      });

      // Prepare request body for backend API
      const requestBodyPayload: any = {
        to: normalizedTo,
        text: text,
        from: normalizedFrom
      };

      const requestBody = JSON.stringify(requestBodyPayload);

      // Send POST request to backend server
      const internalKey = this.getInternalKey();
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${internalKey}`
        },
        body: requestBody
      });

      if (!response.ok) {
        // Try to get error data as both JSON and text
        let errorData: any = {};
        let errorText = '';
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorText = await response.text();
          }
        } catch (e) {
          errorText = await response.text().catch(() => 'Unknown error');
        }
        
        console.error('SMS API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData,
          errorText: errorText,
          headers: Object.fromEntries(response.headers.entries()),
          url: response.url
        });
        
        // Use text error if JSON parsing failed or returned empty
        const errorMessage = errorText || errorData.message || errorData.error || response.statusText;
        const errorCode = errorData.code || '';
        
        throw new Error(`SMS API error: ${response.status} - ${errorMessage}${errorCode ? ` (Code: ${errorCode})` : ''}`);
      }

      const responseData = await response.json();
      
      console.log('SMS sent successfully via backend server:', {
        record_id: responseData.record_id || responseData.id,
        status: responseData.status,
        to: responseData.to,
        from: responseData.from
      });
      
      return {
        success: true,
        message: 'SMS sent successfully',
        record_id: responseData.record_id || responseData.id,
        status: responseData.status
      };

    } catch (error: any) {
      console.error('Error sending SMS via API:', error);
      return {
        success: false,
        message: 'Failed to send SMS',
        error: error.message
      };
    }
  }
}

export const smsApiService = new SMSApiService();
export default smsApiService;
